import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

// コンポーネントのpropsの型定義
interface ImageViewerProps {
  imageData: Array<{page: number, data: string}> | null;
  width?: number;
  height?: number;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ 
  imageData,
  width = 600,
  height = 800
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  
  // 現在の画像データを取得
  const getCurrentImageData = (): string | null => {
    if (!imageData || imageData.length === 0) return null;
    
    const currentPageData = imageData.find(item => item.page === currentPage);
    return currentPageData ? currentPageData.data : null;
  };
  
  // 総ページ数を取得
  const getTotalPages = (): number => {
    return imageData ? imageData.length : 0;
  };
  
  // ページを変更
  const changePage = (newPage: number): void => {
    if (newPage >= 1 && newPage <= getTotalPages()) {
      setLoading(true);
      setCurrentPage(newPage);
      // 画像の読み込みが完了したらローディング状態を解除
      setTimeout(() => setLoading(false), 100);
    }
  };
  
  // 前のページへ
  const previousPage = (): void => changePage(currentPage - 1);
  
  // 次のページへ
  const nextPage = (): void => changePage(currentPage + 1);
  
  // 画像読み込み完了時の処理
  const handleImageLoad = (): void => {
    setLoading(false);
  };
  
  // 画像読み込みエラー時の処理
  const handleImageError = (): void => {
    setLoading(false);
  };
  
  if (!imageData || imageData.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: height || 500,
          width: '100%',
          border: '1px solid #ddd',
          borderRadius: 1,
          bgcolor: '#f5f5f5'
        }}
      >
        <Typography variant="body1" color="text.secondary">
          画像データがありません
        </Typography>
      </Box>
    );
  }
  
  const currentImageData = getCurrentImageData();
  
  return (
    <Box 
      sx={{ 
        border: '1px solid #ddd', 
        borderRadius: 1, 
        overflow: 'hidden',
        width: '100%',
        maxWidth: width || '100%'
      }}
    >
      {/* ナビゲーションバー - 複数ページがある場合のみ表示 */}
      {getTotalPages() > 1 && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            bgcolor: 'background.paper',
            p: 1, 
            borderBottom: '1px solid #ddd'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              onClick={previousPage} 
              disabled={currentPage <= 1}
              size="small"
              sx={{ minWidth: 0, p: 1, mr: 1 }}
            >
              <NavigateBeforeIcon />
            </Button>
            <Button 
              onClick={nextPage} 
              disabled={currentPage >= getTotalPages()}
              size="small"
              sx={{ minWidth: 0, p: 1, mr: 2 }}
            >
              <NavigateNextIcon />
            </Button>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {currentPage} / {getTotalPages()}
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* 画像表示エリア */}
      <Box 
        sx={{ 
          height: height || 500, 
          bgcolor: '#f5f5f5', 
          overflowY: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        {loading && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
        {currentImageData ? (
          <img 
            src={`data:image/jpeg;base64,${currentImageData}`}
            alt={`Labor conditions page ${currentPage}`}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain'
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        ) : (
          <Alert severity="error" sx={{ width: '100%', maxWidth: 400, m: 2 }}>
            画像データの読み込みに失敗しました
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default ImageViewer;